import React, { useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateInventorySite } from '../../../actions/inventory';
import { closeModalAction, openCustomModal } from '../../../actions/modal';
import { actualValueForDropdown, findPincode } from '../../../utils/common';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import Branch from '../../../components/Inputs/Branch';
import Enduser from '../../../components/Inputs/Enduser';
import Address, { transformAddress } from '../../../components/Inputs/Address';
import RenderContact from '../../../components/Inputs/Contact';
import AddNewPincode from '../../Ticket/Modal/AddNewPincode';
const formName = 'editInventorySite';

const EditInventorySite = ({ modalData, source, ticketId }) => {
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);
    const lastSubmitDataRef = useRef(null);
    // Holds { pincode } parsed from the address when the update fails because the pincode isn't registered; null otherwise.
    const [pincodeError, setPincodeError] = useState(null);

    const defaultValues = {
        enduser: modalData.end_user_id,
        branch: modalData.site_branch_name,
        external_id: modalData.external_id,
        address: modalData.address,
        region: modalData.region,
        zone: modalData.zone,
    };

    const submit = (form) => {
        lastSubmitDataRef.current = form;
        setPincodeError(null);
        let contact = [];
        if (actualValueForDropdown(form?.contact) === 'add-new-contact') {
            let own = {};
            if (form[`contact:name`]?.trim()) {
                own.name = form[`contact:name`]
            }
            if (form[`contact:email`]) {
                own.email = form[`contact:email`]
            }
            if (form[`contact:mobile`]) {
                own.mobile = form[`contact:mobile`]
            }
            if (Object.keys(own).length > 0) contact.push(own);
        } else if (form.contact) {
            if (Array.isArray(form.contact)) {
                form.contact.forEach(item => {
                    const findIfExists = modalData.owners.find(owner => owner.owner_id === item.id);
                    if (!findIfExists) {
                        contact.push({
                            id: item.id,
                            name: item.name,
                            email: item.email,
                            mobile: item.mobile
                        });
                    }
                });
            } else {
                const findIfExists = modalData.owners.find(owner => owner.owner_id === form.contact.id);
                if (!findIfExists) {
                    contact.push({
                        id: form.contact.id,
                        name: form.contact.name,
                        email: form.contact.email,
                        mobile: form.contact.mobile
                    });
                }
            }
        }
        let branch_id;
        let branch;
        if (actualValueForDropdown(form.site_branch_name) === 'add-new-branch') {
            branch = form[`site_branch_name:new`];
        } else {
            branch_id = actualValueForDropdown(form.site_branch_name);
        }

        let payload = {
            address: transformAddress(form, 'address'),
            external_id: form.external_id,
            owners: contact,
            region: form.region,
            zone: form.zone,
            branch_id,
            branch,
            end_user_id: actualValueForDropdown(form.enduser),
        };
        if (payload.owners.length === 0) delete payload.owners;
        if (modalData.address === payload.address.plain_text) {
            delete payload.address;
        }
        const onError = (message) => {
            const isPincodeNotFound = message && /pincode/i.test(message) && /(find|exist|not found)/i.test(message);
            if (isPincodeNotFound) {
                const address = form.address || '';
                const parsedPincode = address ? findPincode(address) : '';
                setPincodeError({ pincode: parsedPincode });
            }
        };
        dispatch(updateInventorySite({ id: modalData.id, payload, formName, source, ticketId, onError }))
    };
    const closeModal = () => {
        dispatch(closeModalAction());
    };

    const handlePincodeAdded = () => {
        setPincodeError(null);
        if (lastSubmitDataRef.current) {
            submit(lastSubmitDataRef.current);
        }
    };

    const openAddPincodeModal = () => {
        dispatch(
            openCustomModal({
                heading: 'Add New Pincode',
                modalWidth: '40rem',
                modalHeight: 'auto',
                modalComponent: (
                    <AddNewPincode
                        pincode={pincodeError?.pincode || ''}
                        onSuccess={() => setPincodeError(null)}
                        onAddAndContinue={handlePincodeAdded}
                        addAndContinueText='Add & Update Site'
                    />
                ),
            })
        );
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    return (
        <>
            <Form
                className='w-full'
                onSubmit={(e) => {
                    submit(e);
                }}
                onCancel={closeModal}
                defaultValues={defaultValues}
                submitButtonText={'Update'}
                showSubmit
                submitContainerClassName='flex items-center gap-2 justify-end py-4 px-4'
                isLoading={formButtonLoading[formName]}
                methods={methods}
                renderButtons={pincodeError && (
                    <p className='text-scogoclosed text-font09 font-normal mr-auto'>
                        This pincode isn't registered in the system.{' '}
                        <span
                            onClick={openAddPincodeModal}
                            className='text-scogoprimary font-medium cursor-pointer underline'
                        >
                            Add this pincode
                        </span>
                    </p>
                )}
            >
                {source === 'edit_ticket' && <p className='text-scogoorange text-font13 font-medium px-4 pb-1'>On changing the details of an existing site, all linked ticket sites will also be updated.</p>}
                <EditSiteInputs org_id={modalData?.org_id} location={modalData} source={source} />
            </Form>
        </>
    );
};

export default EditInventorySite;

const EditSiteInputs = ({ org_id, location, source }) => {
    const methods = useForm({ mode: 'all' });

    const handleAddressChange = (validatedAddress) => {
        methods.setValue('address', validatedAddress);
    };

    const prefillOwners = useMemo(() => {
        let prefill = [];
        if (location?.owners) {
            location?.owners?.forEach(({ owner_id, name, mobile, email }) => {
                prefill.push(({ value: owner_id, label: `${name || ''} - (${mobile || ''})`, id: owner_id, email: email, mobile: mobile, name: name }));
            });
        }
        return prefill.length > 0 ? prefill : [];
    }, [location]);

    const preFillEndUser = useMemo(() => {
        return location?.end_user_id ? [location?.end_user_id] : []
    }, [location?.end_user_id]);

    const preFillBranch = useMemo(() => {
        return location?.branch_id ? [location?.branch_id] : []
    }, [location?.branch_id]);

    return (
        <div className='px-3'>
            <div className='grid grid-cols-3 rows-fr grid-flow-row-dense'>
                <Input label='External Site Id' name='external_id' type={'text'} placeholder={'External Site Id'} required />
                <Enduser cleanup={source !== 'edit_ticket'} className={'w-full'} name='enduser' label='Enduser' org_id={actualValueForDropdown(org_id)} prefill={preFillEndUser} />
                <Branch cleanup={source !== 'edit_ticket'} add_new name='site_branch_name' label='Branch Name' org_id={actualValueForDropdown(org_id)} className={'w-full'} prefill={preFillBranch} />
                <Input label='Region' name='region' />
                <Input label='Zone' name='zone' />
                <div>
                    <Address
                        id="address"
                        name="address"
                        label="Site Address"
                        required={true}
                        placeholder="Type your address..."
                        onChange={handleAddressChange}
                    />
                </div>
                <RenderContact cleanup={source !== 'edit_ticket'} name={'contact'} label={'Contact Name'} org_id={actualValueForDropdown(org_id)} prefillObj={prefillOwners} />
            </div>
        </div>
    );
};